import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, StickyNote } from 'lucide-react';
import { useTaskNotes, addTaskNote, NOTE_CATEGORIES, NOTE_CATEGORY_MAP } from '../../hooks/useTaskNotes';
import { useAuth } from '../../context/AuthContext';
import { useToast } from './Toast';
import './TaskNotes.css';

function relTime(ts, t) {
  if (!ts) return '';
  const d = ts?.toDate?.() ?? new Date(ts);
  if (isNaN(d)) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return t('time.justNow');
  if (s < 3600) return t('time.minutesAgo', { count: Math.floor(s / 60) });
  if (s < 86400) return t('time.hoursAgo', { count: Math.floor(s / 3600) });
  return t('time.daysAgo', { count: Math.floor(s / 86400) });
}

function CategoryBadge({ categoryKey }) {
  const { t } = useTranslation();
  const cat = NOTE_CATEGORY_MAP[categoryKey];
  if (!cat) return <span className="note-cat-badge note-cat-neutral">{categoryKey}</span>;
  return (
    <span className={`note-cat-badge note-cat-${cat.color}`}>
      {t(cat.labelKey)}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const { t } = useTranslation();
  if (!severity) return null;
  const label =
    severity === 'high'   ? t('notes.highImpact')   :
    severity === 'medium' ? t('notes.mediumImpact')  :
    t('notes.lowImpact');
  return <span className={`note-sev-badge note-sev-${severity}`}>{label}</span>;
}

function NoteTimeline({ notes }) {
  const { t } = useTranslation();
  if (notes.length === 0) return null;
  return (
    <div className="note-timeline">
      {notes.map((note) => {
        const cat = NOTE_CATEGORY_MAP[note.category];
        const colorClass = cat ? `note-item--${cat.color}` : '';
        return (
          <div key={note.id} className={`note-item ${colorClass}`}>
            <div className="note-item-header">
              <CategoryBadge categoryKey={note.category} />
              {note.severity && <SeverityBadge severity={note.severity} />}
              <span className="note-item-time">{relTime(note.createdAt, t)}</span>
            </div>
            {note.noteText && (
              <p className="note-item-text">{note.noteText}</p>
            )}
            {note.userDisplayName && (
              <span className="note-item-author">{note.userDisplayName}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddNoteForm({ taskId, taskCreatedBy, task, onSaved, compact }) {
  const { t } = useTranslation();
  const { user, companyId, profile } = useAuth();
  const toast = useToast();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [severity, setSeverity] = useState('');
  const [showSeverity, setShowSeverity] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedCategory || saving) return;
    setSaving(true);
    try {
      await addTaskNote({
        companyId,
        taskId,
        taskCreatedBy: taskCreatedBy || user.uid,
        userId: user.uid,
        userDisplayName: profile?.displayName || null,
        category: selectedCategory,
        noteText,
        severity: severity || null,
        task,
      });
      setSelectedCategory(null);
      setNoteText('');
      setSeverity('');
      setShowSeverity(false);
      onSaved?.();
    } catch (err) {
      toast(t('notes.saveFailed'), 'error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryClick = (key) => {
    setSelectedCategory(selectedCategory === key ? null : key);
  };

  return (
    <div className={`add-note-form ${compact ? 'add-note-form--compact' : ''}`}>
      <p className="add-note-prompt">{t('notes.whatHappened')}</p>
      <div className="note-category-grid">
        {NOTE_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            className={`note-cat-chip${selectedCategory === cat.key ? ' selected' : ''}`}
            onClick={() => handleCategoryClick(cat.key)}
          >
            {t(cat.labelKey)}
          </button>
        ))}
      </div>

      {selectedCategory && (
        <div className="note-text-section">
          <textarea
            className="form-control note-textarea"
            placeholder={t('notes.addDetail')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value.slice(0, 500))}
            rows={2}
          />
          {noteText.length > 400 && (
            <span className="note-char-count">{500 - noteText.length} {t('notes.charsLeft')}</span>
          )}

          <button
            type="button"
            className="note-severity-toggle"
            onClick={() => setShowSeverity((v) => !v)}
          >
            {showSeverity ? t('notes.hideImpact') : t('notes.addImpact')}
          </button>

          {showSeverity && (
            <div className="note-severity-chips">
              {['low', 'medium', 'high'].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`note-sev-chip note-sev-chip-${s}${severity === s ? ' selected' : ''}`}
                  onClick={() => setSeverity(severity === s ? '' : s)}
                >
                  {s === 'high' ? t('notes.high') : s === 'medium' ? t('notes.medium') : t('notes.low')}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedCategory && (
        <div className="note-form-actions">
          <p className="note-disclaimer">{t('notes.disclaimer')}</p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <span className="spinner" /> : t('notes.saveNote')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * TaskNotes — main export.
 *
 * Props:
 *   taskId        string   (required)
 *   taskCreatedBy string   uid of the user who created the task
 *   readonly      bool     show notes timeline only, no add form
 *   collapsible   bool     wrap the whole panel in a toggle
 *   defaultOpen   bool     (when collapsible) start expanded
 *   compact       bool     tighter layout for inline embedding
 */
export default function TaskNotes({
  taskId,
  taskCreatedBy,
  task,
  readonly = false,
  collapsible = false,
  defaultOpen = false,
  compact = false,
}) {
  const { t } = useTranslation();
  const { companyId } = useAuth();
  const { notes, loading } = useTaskNotes(taskId, companyId);
  const [open, setOpen] = useState(defaultOpen);
  const [saved, setSaved] = useState(false);

  const handleSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const noteCount = notes.length;
  const hasNotes = noteCount > 0;

  if (collapsible) {
    return (
      <div className={`task-notes-panel task-notes-collapsible ${open ? 'is-open' : ''}`}>
        <button
          type="button"
          className="task-notes-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="task-notes-toggle-left">
            <StickyNote size={14} />
            <span>{t('notes.taskNotes')}</span>
            {hasNotes && (
              <span className="task-notes-count">{noteCount}</span>
            )}
            {saved && <span className="task-notes-saved">{t('notes.saved')}</span>}
          </span>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>

        {open && (
          <div className="task-notes-body">
            {!readonly && (
              <AddNoteForm
                taskId={taskId}
                taskCreatedBy={taskCreatedBy}
                task={task}
                onSaved={handleSaved}
                compact={compact}
              />
            )}
            {loading ? (
              <div className="note-loading"><span className="spinner spinner-sm" /></div>
            ) : (
              <NoteTimeline notes={notes} />
            )}
            {!hasNotes && !loading && readonly && (
              <p className="note-empty">{t('notes.noNotes')}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="task-notes-panel">
      {!readonly && (
        <AddNoteForm
          taskId={taskId}
          taskCreatedBy={taskCreatedBy}
          task={task}
          onSaved={handleSaved}
          compact={compact}
        />
      )}
      {saved && <p className="task-notes-saved-inline">{t('notes.noteSaved')}</p>}
      {loading ? (
        <div className="note-loading"><span className="spinner spinner-sm" /></div>
      ) : (
        <NoteTimeline notes={notes} />
      )}
      {hasNotes && (
        <p className="note-count-label">{t('notes.noteCount', { count: noteCount })}</p>
      )}
      {!hasNotes && !loading && readonly && (
        <p className="note-empty">{t('notes.noNotes')}</p>
      )}
    </div>
  );
}

/** Compact read-only inline note count + last category for table/feed rows. */
export function NoteCountBadge({ notes }) {
  const { t } = useTranslation();
  if (!notes || notes.length === 0) return null;
  const last = notes[notes.length - 1];
  const cat = NOTE_CATEGORY_MAP[last?.category];
  return (
    <span className="note-count-badge" title={t('productionMeta.noteCountTooltip', { count: notes.length })}>
      <StickyNote size={11} />
      {notes.length}
      {cat && <span className="note-count-badge-cat">{t(cat.labelKey)}</span>}
    </span>
  );
}
